import React, { useMemo } from 'react';

import { Stack } from '@atlaskit/primitives/compiled';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { type GlobalShortcutsProps, ShortcutMenuItem } from '@atlassian/global-shortcuts';
import { RequestStatus, type ProductLink } from '@atlassian/navigation-bff-data-provider';

import type { MappedGlobalShortcutsItem } from './types';

export interface ShortcutsMenuProps extends Pick<
	GlobalShortcutsProps,
	'shortcutItems' | 'forceStatic' | 'product' | 'openInSameTab'
> {
	invitedAppProductLinks: Record<string, ProductLink[]>;
}

export const ShortcutsMenu = ({
	shortcutItems = [],
	forceStatic,
	product,
	openInSameTab,
	invitedAppProductLinks,
}: ShortcutsMenuProps) => {
	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems
			.map((item) => {
				if (item.type === 'link' && item.apsKey) {
					const key = item.apsKey;
					const filteredProductLinks = invitedAppProductLinks[item.apsKey];
					// By the time we got here, we've already decided we're eligible etc.,
					// so we had decided to show our section above page tree and remove Jira from shortcuts section.
					// Bailing out here does not restore control behavior, and we're not firing any telemetry.
					// This is a defensive design from the architecture point of view. Since the data is provided by GAS.
					if (!filteredProductLinks) {
						return {
							...item,
							key,
						};
					}

					// we only want to take single link items as we are not supporting expand menus
					if (filteredProductLinks.length === 1 && !item.url) {
						return {
							...item,
							key,
							url: filteredProductLinks[0].url,
						};
					}
				}

				return null;
			})
			.filter(Boolean) as Array<MappedGlobalShortcutsItem>;
	}, [shortcutItems, invitedAppProductLinks]) as Array<MappedGlobalShortcutsItem>;

	const loading = Object.keys(invitedAppProductLinks).length === 0;

	return (
		<Stack testId="shortcuts-menu">
			<MenuList>
				{mappedShortcutItems.map((shortcut) => (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						forceStatic={loading ? false : forceStatic}
						status={loading ? RequestStatus.LOADING : undefined}
						product={product}
						openInSameTab={openInSameTab}
					/>
				))}
			</MenuList>
		</Stack>
	);
};
